// frontend/src/pages/RestDayMonitoring/hooks/useRestDayData.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../../services/api';
import toast from '../../../utils/toast';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Custom hook for centralized rest day interval monitoring data fetching.
 * 
 * Mirrors useOvertimeData.js pattern for consistency.
 * 
 * Uses endpoints:
 * - /staff-core/rest-day-monitoring/stats/
 * - /staff-core/rest-day-monitoring/employees/
 * - /staff-core/rest-day-monitoring/alerts/
 * - /staff-core/rest-day-monitoring/employee/<staff_id>/detail/
 * 
 * Features:
 * - Summary stats for dashboard cards
 * - Employee list with compliance status + filters
 * - Active alerts with acknowledge
 * - Individual employee 30-day timeline detail
 */
const useRestDayData = () => {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    department: '',
    search: '',
  });

  // ── Fetch functions ──────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.getRestDayMonitoringStats();
      setStats(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching rest day stats:', err);
      throw err;
    }
  }, []);

  const fetchEmployees = useCallback(async (filterParams = {}) => {
    try {
      const params = {
        status: filterParams.status || filters.status,
        department: filterParams.department || filters.department,
        search: filterParams.search || filters.search,
      };
      
      // Remove empty params
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const response = await api.getRestDayEmployeesStatus(params);
      setEmployees(response.data.employees || []);
      return response.data;
    } catch (err) {
      console.error('Error fetching rest day employees:', err);
      throw err;
    }
  }, [filters]);

  const fetchAlerts = useCallback(async (filterParams = {}) => {
    try {
      const params = {};
      if (filterParams.severity) params.severity = filterParams.severity;
      if (filterParams.alert_type) params.alert_type = filterParams.alert_type;
      if (filterParams.status) params.status = filterParams.status;
      
      const response = await api.getRestDayActiveAlerts(params);
      setAlerts(response.data.alerts || []);
      return response.data;
    } catch (err) {
      console.error('Error fetching rest day alerts:', err);
      throw err;
    }
  }, []);

  // ── Load all data ────────────────────────────────────────────────────────

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchStats(),
        fetchEmployees(),
        fetchAlerts(),
      ]);
    } catch (err) {
      setError(err.message || 'Failed to load rest day monitoring data');
      toast.error('Failed to load rest day monitoring data');
    } finally {
      setLoading(false);
    }
  }, [fetchStats, fetchEmployees, fetchAlerts]);

  const refetch = useCallback(() => {
    loadAllData();
  }, [loadAllData]);

  // ── Filter management ────────────────────────────────────────────────────

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const changeMonth = useCallback((yearMonth) => {
    setSelectedMonth(yearMonth);
  }, []);

  const applyFilters = useCallback(async () => {
    setLoading(true);
    try {
      await fetchEmployees(filters);
    } catch (err) {
      toast.error('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  }, [fetchEmployees, filters]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const acknowledgeAlert = useCallback(async (alertId, staffId, notes = '') => {
    try {
      await api.acknowledgeRestDayAlert(alertId, {
        staff_id: staffId,
        notes,
      });
      toast.success('Alert acknowledged successfully');
      await fetchAlerts();
      return true;
    } catch (err) {
      console.error('Error acknowledging rest day alert:', err);
      toast.error('Failed to acknowledge alert');
      return false;
    }
  }, [fetchAlerts]);

  const getEmployeeDetail = useCallback(async (staffId) => {
    try {
      const response = await api.getRestDayEmployeeDetail(staffId);
      return response.data;
    } catch (err) {
      console.error('Error fetching rest day employee detail:', err);
      toast.error('Failed to load employee detail');
      return null;
    }
  }, []);

  const getNotificationCoverage = useCallback(async (params = {}) => {
    try {
      const response = await api.getRestDayNotificationCoverage(params);
      return response.data;
    } catch (err) {
      console.error('Error fetching rest day notification coverage:', err);
      toast.error('Failed to load notification coverage');
      return null;
    }
  }, []);

  // ── Initial load ─────────────────────────────────────────────────────────

  useEffect(() => {
    loadAllData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch employees when filters change
  useEffect(() => {
    if (!loading && stats) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.department]);

  // ── Return ───────────────────────────────────────────────────────────────

  return {
    // Data
    stats,
    employees,
    alerts,
    loading,
    error,
    filters,
    selectedMonth,
    monthOptions,
    
    // Actions
    refetch,
    updateFilters,
    applyFilters,
    changeMonth,
    acknowledgeAlert,
    getEmployeeDetail,
    getNotificationCoverage,
  };
};

export default useRestDayData;
